/**
 * Port of astro_calculations/astrology/transit_data.py.
 *
 * The live lib/ engine uses only get_divisional_position (lib/divisional_scoring.py,
 * lib/traits.py via _helpers). The TransitData class is ALSO ported here because the
 * legacy fallback scorer (static_predictions.get_predictions → legacy_engine) builds
 * its charts through it — Equal-house ('E') natal/transit charts, NOT the live
 * engine's Placidus/Whole-sign charts.
 */

import * as swe from "./ephemeris";
import { pymod, pythonRound, zfill } from "../lib/pyutil";
import type { PlainDateTime } from "../lib/datetime";
import { addSeconds, diffSeconds } from "../lib/datetime";
import { pyfloordiv } from "./pyfloat";

/**
 * Convert decimal longitude (0–360) into degrees°minutes′ inside its sign.
 * Example: 140.43 → "20°26′"
 */
export function dec_to_dms(longitude: number): string {
  const lon = pymod(longitude, 360);
  const sign_pos = pymod(lon, 30);
  let deg = Math.trunc(sign_pos);
  let minutes = Math.trunc(pythonRound((sign_pos - deg) * 60));
  if (minutes === 60) {
    deg += 1;
    minutes = 0;
  }
  if (deg === 30) {
    deg = 0;
  }
  return `${deg}°${zfill(minutes, 2)}′`;
}

/**
 * Convert degrees+minutes within a sign back to decimal degrees.
 * Example: 20°26′ → 20.43
 */
export function dms_to_dec(deg: number, minutes: number): number {
  return deg + minutes / 60.0;
}

/**
 * Normalize longitude to 0-360 degrees.
 */
export function degnorm(x: number): number {
  return pymod(x, 360.0);
}

/**
 * Given a planet's longitude (0-360), return its position in the divisional chart.
 * For proper Vedic divisional charts, each division maps to specific signs.
 * Returns (divisional_sign, degrees_in_divisional_sign)
 *
 * CORRECTED: Traditional Vedic divisional chart calculations with proper odd/even handling
 *
 * PORT QUIRK (verified against fixtures): despite the docstring above, the second
 * value returned is the ABSOLUTE divisional longitude `divisional_sign * 30 +
 * divisional_degrees`, not degrees-in-sign. Ported exactly.
 */
export function get_divisional_position(lon: number, division: number): [number, number] {
  const sign = Math.trunc(Math.floor(lon / 30)); // 0=Aries, 1=Taurus, etc.
  const sign_pos = pymod(lon, 30); // Position within the sign (0-30 degrees)

  // Calculate which divisional part within the sign.
  // Exact 0 degrees belongs to the first part, not the previous/last part.
  // pyfloordiv: part_size = 30/division is inexact for most divisions, and Python's
  // fmod-based `//` differs from Math.floor(a / b) exactly at part boundaries.
  const part_size = 30.0 / division;
  const part = Math.max(0, Math.min(Math.trunc(pyfloordiv(sign_pos, part_size)), division - 1));

  // CRITICAL FIX: In Vedic astrology, signs numbered 0,2,4,6,8,10 (Aries,Gemini,Leo,Libra,Sag,Aquarius) are ODD
  // Signs numbered 1,3,5,7,9,11 (Taurus,Cancer,Virgo,Scorpio,Cap,Pisces) are EVEN
  const is_odd_sign = pymod(sign, 2) === 0; // 0=Aries is odd, 1=Taurus is even

  let divisional_sign: number;
  let divisional_degrees: number;

  // Map to divisional sign based on division rules
  if (division === 1) {
    // D1 - Rasi (main chart)
    divisional_sign = sign;
    divisional_degrees = sign_pos;
  } else if (division === 2) {
    // D2 - Hora (wealth)
    // FIXED: Odd signs (Aries,Gemini,Leo...): Leo then Cancer
    // Even signs (Taurus,Cancer,Virgo...): Cancer then Leo
    if (is_odd_sign) {
      divisional_sign = part === 0 ? 4 : 3; // Leo (4) then Cancer (3)
    } else {
      divisional_sign = part === 0 ? 3 : 4; // Cancer (3) then Leo (4)
    }
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 3) {
    // D3 - Drekkana (siblings, courage)
    // BPHS/Brihat Jataka: the three drekkanas are the 1st, 5th,
    // and 9th signs from the source sign.
    const offsets = [0, 4, 8];
    divisional_sign = pymod(sign + offsets[part], 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 4) {
    // D4 - Chaturthamsa (property, fortune)
    // BPHS: the four divisions are ruled by the four kendras from
    // the source sign.
    divisional_sign = pymod(sign + part * 3, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 5) {
    // D5 - Panchamsa (fame, power, authority)
    // Odd signs: start from own sign
    // Even signs: start from 9th sign (8 signs away)
    const start = is_odd_sign ? sign : pymod(sign + 8, 12);
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 6) {
    // D6 - Shashthamsa (health, disease, enemies)
    // FIXED: Proper D6 calculation
    // Odd signs: start from own sign
    // Even signs: start from 7th sign (6 signs away)
    const start = is_odd_sign ? sign : pymod(sign + 6, 12);
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 7) {
    // D7 - Saptamsa (children, grandchildren)
    // FIXED: Odd signs start from own sign; Even signs start from 7th (6 signs away)
    const start = is_odd_sign ? sign : pymod(sign + 6, 12);
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 8) {
    // D8 - Ashtamsa (longevity, obstacles, sudden events)
    // Based on sign quality
    const sign_quality = pymod(sign, 3);
    let start: number;
    if (sign_quality === 0) {
      // Movable: start from own sign
      start = sign;
    } else if (sign_quality === 1) {
      // Fixed: start from 4th sign (3 signs away)
      start = pymod(sign + 3, 12);
    } else {
      // Dual: start from 7th sign (6 signs away)
      start = pymod(sign + 6, 12);
    }
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 9) {
    // D9 - Navamsa (most important divisional)
    // Traditional Navamsa: each sign divided into 9 parts (3°20' each)
    // Mapping depends on sign's element (triplicity)
    const sign_element = pymod(sign, 4); // 0,4,8=Fire; 1,5,9=Earth; 2,6,10=Air; 3,7,11=Water

    if (sign_element === 0) {
      // Fire signs: Aries, Leo, Sagittarius
      divisional_sign = pymod(0 + part, 12); // Start from Aries
    } else if (sign_element === 1) {
      // Earth signs: Taurus, Virgo, Capricorn
      divisional_sign = pymod(9 + part, 12); // Start from Capricorn
    } else if (sign_element === 2) {
      // Air signs: Gemini, Libra, Aquarius
      divisional_sign = pymod(6 + part, 12); // Start from Libra
    } else {
      // Water signs: Cancer, Scorpio, Pisces
      divisional_sign = pymod(3 + part, 12); // Start from Cancer
    }

    // Position within the navamsa sign (proportionally scaled)
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 10) {
    // D10 - Dasamsa (career, profession)
    // FIXED: Odd signs start from own sign; Even signs start from 9th (8 signs away)
    const start = is_odd_sign ? sign : pymod(sign + 8, 12);
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 11) {
    // D11 - Rudramsa (destruction, inheritance)
    // FIXED: Proper D11 calculation
    // Odd signs: start from own sign
    // Even signs: start from own sign
    // Each division represents specific life events/destructions
    divisional_sign = pymod(sign + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 12) {
    // D12 - Dwadasamsa (parents)
    // Simple: start from own sign for all
    divisional_sign = pymod(sign + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 16) {
    // D16 - Shodasamsa (vehicles, comforts)
    // BPHS: start from Aries for movable, Leo for fixed,
    // Sagittarius for dual, then distribute sequentially.
    const sign_quality = pymod(sign, 3);
    const start = [0, 4, 8][sign_quality];
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 20) {
    // D20 - Vimsamsa (spiritual pursuits)
    // BPHS: start from Aries for movable, Sagittarius for fixed,
    // Leo for dual/common signs.
    const sign_quality = pymod(sign, 3);
    let start: number;
    if (sign_quality === 0) {
      start = 0; // Aries
    } else if (sign_quality === 1) {
      start = 8; // Sagittarius
    } else {
      start = 4; // Leo
    }
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 24) {
    // D24 - Siddhamsa (education, learning)
    // BPHS: start from Leo in odd signs and Cancer in even signs.
    const start = is_odd_sign ? 4 : 3;
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 27) {
    // D27 - Saptavimshamsa / Bhamsa (strength, vitality).
    // 03_varga_calculation_methods.md (D-27): "Counting begins from the respective MOVABLE sign
    // ... all 12 signs are anchored to their movable-sign starting point." Each sign starts from
    // the movable (cardinal) sign of ITS OWN element:
    //   Fire signs (0,4,8) → Aries (0); Earth signs (1,5,9) → Capricorn (9);
    //   Air signs (2,6,10) → Libra (6); Water signs (3,7,11) → Cancer (3).
    const sign_element = pymod(sign, 4);
    const start_signs = [0, 9, 6, 3]; // fire→Aries, earth→Capricorn, air→Libra, water→Cancer
    const start = start_signs[sign_element];
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 30) {
    // D30 - Trimsamsa (evil, misfortune) - PROPER UNEQUAL DIVISIONS
    // Traditional D30 uses unequal 5-degree segments
    // Odd signs: Mars(5°), Saturn(5°), Jupiter(8°), Mercury(7°), Venus(5°)
    // Even signs: Venus(5°), Mercury(7°), Jupiter(8°), Saturn(5°), Mars(5°)

    // Define segment boundaries (cumulative degrees)
    let boundaries: number[];
    let lords_signs: number[];
    if (is_odd_sign) {
      // Odd: Mars, Saturn, Jupiter, Mercury, Venus
      boundaries = [5, 10, 18, 25, 30];
      lords_signs = [0, 10, 8, 2, 6]; // Aries, Aquarius, Sagittarius, Gemini, Libra
    } else {
      // Even: Venus, Mercury, Jupiter, Saturn, Mars
      boundaries = [5, 12, 20, 25, 30];
      lords_signs = [1, 5, 11, 9, 7]; // Taurus, Virgo, Pisces, Capricorn, Scorpio
    }

    // Find which segment the planet falls in
    let segment = 0;
    for (let i = 0; i < boundaries.length; i++) {
      if (sign_pos < boundaries[i]) {
        segment = i;
        break;
      }
    }

    divisional_sign = lords_signs[segment];
    // Calculate position within the trimsamsa (proportional)
    const prev_boundary = segment === 0 ? 0 : boundaries[segment - 1];
    const segment_size = boundaries[segment] - prev_boundary;
    divisional_degrees = ((sign_pos - prev_boundary) / segment_size) * 30;
  } else if (division === 40) {
    // D40 - Khavedamsa (auspicious/inauspicious effects)
    // FIXED: Traditional D40 calculation
    // Odd signs: start from Aries
    // Even signs: start from Libra (6 signs away)
    const start = is_odd_sign ? 0 : 6;
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 45) {
    // D45 - Akshavedamsa (general well-being)
    // BPHS: start from Aries for movable, Leo for fixed,
    // Sagittarius for dual/common signs.
    const sign_quality = pymod(sign, 3);
    const start = [0, 4, 8][sign_quality];
    divisional_sign = pymod(start + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else if (division === 60) {
    // D60 - Shashtiamsa (the deepest karmic qualifier; ~0.5°/2-min lagna resolution).
    // Canonical rule (03_varga_calculation_methods.md, D-60): "Ignore the sign; take only degrees
    // within the sign; multiply by 2; divide by 12; remainder + 1 = the Shashtiamsa sign" —
    // counted FROM Aries. `part` is already floor(sign_pos / 0.5) = floor(sign_pos * 2)
    // (part_size = 30/60 = 0.5°); `part mod 12` is the 0-indexed sign, which is exactly the
    // source's 1-indexed "remainder + 1" (the +1 is the 0→1-index conversion, not extra math).
    //
    // This is numerically identical to the previous `(sign * 60 + part) mod 12` form (60 ≡ 0
    // mod 12, so the natal-sign term always vanished) — but that form read as if the result
    // were natal-sign-relative, the mainstream BPHS variant, NOT Vol_03 §2.16. We state the
    // Aries-counted rule directly so the code matches the cited source of truth.
    divisional_sign = pymod(part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  } else {
    // Generic calculation for unsupported divisions
    divisional_sign = pymod(sign * division + part, 12);
    divisional_degrees = pymod(sign_pos, part_size) * (30.0 / part_size);
  }

  // Convert to absolute longitude
  const divisional_lon = divisional_sign * 30 + divisional_degrees;

  return [divisional_sign, divisional_lon];
}

/**
 * For each planet, compute its divisional chart position.
 * Returns a dict: {planet: {"sign": int, "longitude": float, "dms": str}}
 *
 * NOTE: Only applies to planets and Rahu/Ketu. Ascendant can be included in some charts.
 * MC and other sensitive points are NOT traditionally used in divisional charts.
 */
export function get_divisional_chart(
  planet_deg: Record<string, number | null>,
  division: number
): Record<string, { sign: number; longitude: number; dms: string } | null> {
  const result: Record<string, { sign: number; longitude: number; dms: string } | null> = {};
  // FIXED: More strict - only skip MC, Descendant, IC in divisional charts
  // Ascendant CAN be used in some divisional interpretations (D1, D9, D12)
  const skip_points = new Set(["midheaven", "descendant", "imumcoeli"]);

  // However, for D9/D10/D30, even Ascendant should be skipped in traditional Vedic
  // This is a compromise allowing Ascendant in D1 but you may want to reconsider
  if ([9, 10, 30].includes(division)) {
    skip_points.add("ascendant");
  }

  for (const [planet, lon] of Object.entries(planet_deg)) {
    if (lon === null || skip_points.has(planet)) {
      result[planet] = null;
      continue;
    }
    const [div_sign, div_lon] = get_divisional_position(lon, division);
    result[planet] = {
      sign: div_sign,
      longitude: div_lon,
      dms: dec_to_dms(div_lon),
    };
  }
  return result;
}

export interface TransitChartData {
  dms: Record<string, string | null>;
  deg: Record<string, number | null>;
  speed: Record<string, number>;
  retrograde: string[];
}

export class TransitData {
  HOUSE_SYSTEM: string;
  NODE_TYPE: string;
  PLANETS: Array<[string, number]>;

  constructor() {
    // Python sets swe.set_ephe_path(swiss dir) + swe.set_sid_mode(SIDM_LAHIRI) here.
    // In the port both are done once by initEphemeris(); set_ephe_path() asserts init.
    swe.set_ephe_path();
    swe.set_sid_mode(swe.SIDM_LAHIRI);

    // For Vedic: use Equal houses or compute Whole Sign manually
    // 'P' = Placidus (Western), 'K' = Koch, 'E' = Equal, 'B' = Alcabitius
    // NOTE: There is NO 'W' for Whole Sign in Swiss Ephemeris
    //   [Python comment preserved; the live engine's _chart.py does use 'W' successfully.]
    this.HOUSE_SYSTEM = "E"; // Equal house - closest to Whole Sign behavior
    this.NODE_TYPE = "MEAN"; // MEAN nodes are traditional in Vedic

    this.PLANETS = [
      ["Sun", swe.SUN],
      ["Moon", swe.MOON],
      ["Mercury", swe.MERCURY],
      ["Venus", swe.VENUS],
      ["Mars", swe.MARS],
      ["Jupiter", swe.JUPITER],
      ["Saturn", swe.SATURN],
      // Outer planets - non-traditional Vedic but included for modern use
      ["Uranus", swe.URANUS],
      ["Neptune", swe.NEPTUNE],
      ["Pluto", swe.PLUTO],
      ["Chiron", swe.CHIRON],
    ];
    // (Python also checks the swiss dir for .se1 files; initEphemeris guarantees them.)
  }

  /**
   * Compute Whole Sign house cusps from Ascendant longitude.
   * In Whole Sign, house 1 starts at 0° of Ascendant's sign.
   * Returns array of 12 house cusp longitudes.
   */
  compute_whole_sign_houses(asc_lon: number): number[] {
    const asc_sign = Math.trunc(Math.floor(asc_lon / 30));
    const cusps: number[] = [];
    for (let i = 0; i < 12; i++) {
      cusps.push(pymod(asc_sign * 30 + i * 30, 360));
    }
    return cusps;
  }

  /**
   * Generate transit data for a given datetime and location.
   *
   * Args:
   *     dt: NAIVE datetime in LOCAL time (PlainDateTime — always naive in the port)
   *     latitude: Latitude in degrees
   *     longitude: Longitude in degrees
   *     timezone_offset: Hours AHEAD of UTC (e.g., 5.5 for IST)
   */
  _generate_transit_data(
    dt: PlainDateTime,
    latitude: number,
    longitude: number,
    timezone_offset = 5.5
  ): TransitChartData {
    // Convert local naive time to UTC by subtracting timezone offset
    const utc_dt = addSeconds(dt, -timezone_offset * 3600);
    const jd = swe.julday(
      utc_dt.year,
      utc_dt.month,
      utc_dt.day,
      utc_dt.hour + utc_dt.minute / 60.0 + utc_dt.second / 3600.0
    );

    const planet_deg: Record<string, number | null> = {};
    const planet_dms: Record<string, string | null> = {};
    const planet_speed: Record<string, number> = {}; // NEW: longitude speeds (deg/day)
    const retrograde_planets: string[] = [];

    // Planets
    for (const [name, body] of this.PLANETS) {
      const flags = swe.FLG_SIDEREAL | swe.FLG_SPEED;
      const [res] = swe.calc_ut(jd, body, flags);
      const lon = degnorm(res[0]);
      const speed_lon = res[3]; // longitude speed in degrees per day

      // Retrograde check: negative longitudinal speed with small threshold
      const is_retrograde = speed_lon < -0.001;

      const key = name.toLowerCase();
      planet_deg[key] = lon;
      planet_dms[key] = dec_to_dms(lon);
      planet_speed[key] = speed_lon; // NEW

      if (is_retrograde) {
        retrograde_planets.push(key);
      }
    }

    // Rahu & Ketu
    const node_body = this.NODE_TYPE === "MEAN" ? swe.MEAN_NODE : swe.TRUE_NODE;
    const flags = swe.FLG_SIDEREAL | swe.FLG_SPEED;
    const [rahu_res] = swe.calc_ut(jd, node_body, flags);
    const rahu_lon = degnorm(rahu_res[0]);
    const rahu_speed = rahu_res[3]; // NEW
    const ketu_lon = degnorm(rahu_lon + 180);

    planet_deg["rahu"] = rahu_lon;
    planet_dms["rahu"] = dec_to_dms(rahu_lon);
    planet_speed["rahu"] = rahu_speed; // NEW

    planet_deg["ketu"] = ketu_lon;
    planet_dms["ketu"] = dec_to_dms(ketu_lon);
    planet_speed["ketu"] = rahu_speed; // NEW (same as Rahu)

    if (this.NODE_TYPE === "MEAN" || rahu_speed < -0.001) {
      retrograde_planets.push("rahu", "ketu");
    }

    // Ascendant & Midheaven using Equal houses (closest to Vedic Whole Sign)
    let asc: number | null = null;
    let mc: number | null = null;
    try {
      const HOUSE_FLAGS = swe.FLG_SIDEREAL;
      const [, ascmc] = swe.houses_ex(jd, latitude, longitude, this.HOUSE_SYSTEM, HOUSE_FLAGS);
      asc = degnorm(ascmc[0]);
      mc = degnorm(ascmc[1]);

      // Optionally compute true Whole Sign houses
      // whole_sign_cusps = this.compute_whole_sign_houses(asc)
    } catch (e) {
      console.log(`House calculation error: ${e}`);
      asc = mc = null;
    }

    planet_deg["ascendant"] = asc;
    planet_dms["ascendant"] = asc !== null ? dec_to_dms(asc) : null;
    planet_deg["midheaven"] = mc;
    planet_dms["midheaven"] = mc !== null ? dec_to_dms(mc) : null;

    return {
      dms: planet_dms,
      deg: planet_deg,
      speed: planet_speed, // NEW
      retrograde: [...retrograde_planets].sort(),
    };
  }

  /**
   * Get D1 transit data for given datetime and location.
   *
   * Args:
   *     datetime: Local datetime
   *     latitude: Latitude in degrees
   *     longitude: Longitude in degrees
   *     timezone_offset: Hours ahead of UTC (e.g., 5.5 for IST, -5.0 for EST)
   */
  get_transit_data(
    datetime: PlainDateTime,
    latitude: number,
    longitude: number,
    timezone_offset = 0.0
  ): TransitChartData {
    return this._generate_transit_data(datetime, latitude, longitude, timezone_offset);
  }

  /**
   * Calculate current Vimshottari Dasha periods.
   * Returns dict with mahadasha, antardasha, and pratyantar dasha lords.
   *
   * Dasha durations (in years):
   * Ketu: 7, Venus: 20, Sun: 6, Moon: 10, Mars: 7,
   * Rahu: 18, Jupiter: 16, Saturn: 19, Mercury: 17
   */
  get_vimshottari_dasha(birth_dt: PlainDateTime, timezone_offset = 5.5): Record<string, any> {
    // Calculate birth Moon nakshatra
    const utc_dt = addSeconds(birth_dt, -timezone_offset * 3600);
    const jd_birth = swe.julday(
      utc_dt.year,
      utc_dt.month,
      utc_dt.day,
      utc_dt.hour + utc_dt.minute / 60.0 + utc_dt.second / 3600.0
    );

    const [moon_res] = swe.calc_ut(jd_birth, swe.MOON, swe.FLG_SIDEREAL);
    const moon_lon = degnorm(moon_res[0]);

    // Each nakshatra is exactly 13°20' = 360/27 (no truncation, so the dasha
    // balance at birth carries no rounding drift).
    const nakshatra_span = 360.0 / 27.0;
    const nakshatra_num = Math.trunc(moon_lon / nakshatra_span);
    const nakshatra_position = pymod(moon_lon, nakshatra_span) / nakshatra_span; // 0.0 to 1.0 within nakshatra

    // Dasha sequence and durations
    const DASHA_SEQUENCE: Array<[string, number]> = [
      ["ketu", 7], ["venus", 20], ["sun", 6], ["moon", 10], ["mars", 7],
      ["rahu", 18], ["jupiter", 16], ["saturn", 19], ["mercury", 17],
    ];

    // Starting dasha lord based on birth nakshatra
    const start_index = pymod(nakshatra_num, 9);

    // Calculate balance of starting dasha (years remaining at birth)
    const [start_lord, start_duration] = DASHA_SEQUENCE[start_index];
    const balance_years = start_duration * (1.0 - nakshatra_position);

    return {
      mahadasha_lord: start_lord,
      balance_at_birth: balance_years,
      dasha_sequence: DASHA_SEQUENCE,
      start_index: start_index,
    };
  }

  /**
   * Get the current Mahadasha and Antardasha lords for a given date.
   * Returns dict with current period information.
   */
  get_current_dasha(
    birth_dt: PlainDateTime,
    current_dt: PlainDateTime,
    timezone_offset = 5.5
  ): Record<string, any> {
    const dasha_info = this.get_vimshottari_dasha(birth_dt, timezone_offset);

    // Calculate years elapsed since birth. Use the same sidereal-year basis
    // as the Julian-day dasha implementation elsewhere in the codebase.
    const years_elapsed = diffSeconds(current_dt, birth_dt) / (365.25636 * 24 * 60 * 60);

    const DASHA_SEQUENCE = dasha_info["dasha_sequence"] as Array<[string, number]>;
    const start_index = dasha_info["start_index"] as number;
    const balance = dasha_info["balance_at_birth"] as number;

    // Find current mahadasha
    let elapsed = years_elapsed;

    // Subtract balance of first dasha
    if (elapsed < balance) {
      // Still in birth dasha
      const [current_maha_lord, maha_duration] = DASHA_SEQUENCE[start_index];
      const maha_elapsed = maha_duration - balance + elapsed;
      const maha_remaining = balance - elapsed;
      const antardasha_info = this._get_antardasha(current_maha_lord, maha_elapsed, DASHA_SEQUENCE);

      return {
        mahadasha: current_maha_lord,
        antardasha: antardasha_info["lord"],
        maha_years_remaining: maha_remaining,
        antar_years_remaining: antardasha_info["remaining"],
      };
    }

    elapsed -= balance;

    // Cycle through dashas
    let current_index = pymod(start_index + 1, 9);

    while (elapsed >= 0) {
      const [lord, duration] = DASHA_SEQUENCE[current_index];
      if (elapsed < duration) {
        // Found current dasha
        const maha_elapsed = elapsed;
        const maha_remaining = duration - elapsed;
        const antardasha_info = this._get_antardasha(lord, maha_elapsed, DASHA_SEQUENCE);

        return {
          mahadasha: lord,
          antardasha: antardasha_info["lord"],
          maha_years_remaining: maha_remaining,
          antar_years_remaining: antardasha_info["remaining"],
        };
      }

      elapsed -= duration;
      current_index = pymod(current_index + 1, 9);
    }

    // Shouldn't reach here, but return first dasha as fallback
    return {
      mahadasha: DASHA_SEQUENCE[start_index][0],
      antardasha: DASHA_SEQUENCE[start_index][0],
      maha_years_remaining: 0,
      antar_years_remaining: 0,
    };
  }

  /** Calculate current antardasha within a mahadasha. */
  _get_antardasha(
    maha_lord: string,
    maha_elapsed: number,
    dasha_sequence: Array<[string, number]>
  ): Record<string, any> {
    // Find mahadasha in sequence
    const maha_duration = dasha_sequence.find(([l]) => l === maha_lord)![1];

    // Antardasha proportions (each lord's sub-period is proportional)
    const total_proportion = dasha_sequence.reduce((acc, [, d]) => acc + d, 0);

    // Start with mahadasha lord's own antardasha
    const maha_index = dasha_sequence.findIndex(([l]) => l === maha_lord);

    let time_in_maha = maha_elapsed;

    for (let i = 0; i < 9; i++) {
      const antar_index = pymod(maha_index + i, 9);
      const [antar_lord, antar_base_duration] = dasha_sequence[antar_index];

      // Antardasha duration = (antar_base / total) * maha_duration
      const antar_duration = (antar_base_duration / total_proportion) * maha_duration;

      if (time_in_maha < antar_duration) {
        return {
          lord: antar_lord,
          remaining: antar_duration - time_in_maha,
        };
      }

      time_in_maha -= antar_duration;
    }

    // Fallback
    return { lord: maha_lord, remaining: 0 };
  }

  /**
   * Calculate which house each natal planet occupies.
   * Returns dict: {planet: house_number} where house_number is 1-12
   */
  get_natal_house_positions(natal_deg: Record<string, number | null>): Record<string, number> {
    if (!("ascendant" in natal_deg) || natal_deg["ascendant"] === null) {
      return {};
    }

    const asc_lon = natal_deg["ascendant"];
    // const house_cusps = this.compute_whole_sign_houses(asc_lon);  // computed but unused, as in Python
    this.compute_whole_sign_houses(asc_lon);

    const house_positions: Record<string, number> = {};

    for (const [planet, lon] of Object.entries(natal_deg)) {
      if (lon === null) {
        continue;
      }

      // Find which house this planet is in (whole sign)
      const planet_sign = Math.trunc(Math.floor(lon / 30));
      const asc_sign = Math.trunc(Math.floor(asc_lon / 30));

      // House number (1-12) from ascendant
      const house_num = pymod(planet_sign - asc_sign, 12) + 1;
      house_positions[planet] = house_num;
    }

    return house_positions;
  }

  /**
   * Calculate simplified planetary strength (Shadbala approximation).
   * Returns score 0.0 to 1.0
   */
  calculate_planetary_strength(planet_lon: number, planet_name: string): number {
    const sign = Math.trunc(Math.floor(planet_lon / 30));

    // Exaltation/Debilitation signs (simplified)
    const EXALTATIONS: Record<string, number> = {
      sun: 0, // Aries
      moon: 1, // Taurus
      mars: 9, // Capricorn
      mercury: 5, // Virgo
      jupiter: 3, // Cancer
      venus: 11, // Pisces
      saturn: 6, // Libra
    };

    const DEBILITATIONS: Record<string, number> = Object.fromEntries(
      Object.entries(EXALTATIONS).map(([k, v]) => [k, pymod(v + 6, 12)])
    );

    // Own signs
    const OWN_SIGNS: Record<string, number[]> = {
      sun: [4], // Leo
      moon: [3], // Cancer
      mars: [0, 7], // Aries, Scorpio
      mercury: [2, 5], // Gemini, Virgo
      jupiter: [8, 11], // Sagittarius, Pisces
      venus: [1, 6], // Taurus, Libra
      saturn: [9, 10], // Capricorn, Aquarius
    };

    const planet_lower = planet_name.toLowerCase();

    // Calculate strength
    if (planet_lower in EXALTATIONS && sign === EXALTATIONS[planet_lower]) {
      return 1.0; // Exalted
    } else if (planet_lower in DEBILITATIONS && sign === DEBILITATIONS[planet_lower]) {
      return 0.2; // Debilitated
    } else if (planet_lower in OWN_SIGNS && OWN_SIGNS[planet_lower].includes(sign)) {
      return 0.8; // Own sign
    } else {
      return 0.5; // Neutral
    }
  }
}
